import { Component, ViewChild, OnDestroy, inject } from '@angular/core';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { FilterConfig, FiltersConfig } from '@next/core-lib/components';
import { FilterTypes } from '@next/core-lib/components';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';
import { SiteState } from 'src/app/shared/models';

import { SoilAnalysis, State } from '../../models/soil-analysis.models';
import * as SoilAnalysisActions from '../../store/soil-analysis.actions';
import { selectDefaultSoilAnalyses } from '../../store/soil-analysis.selectors';

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss'],
  standalone: false,
})
export class OverviewComponent implements OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);

  @ViewChild('paginator') paginator: MatPaginator;
  destroyed$ = new Subject<void>();
  searchChanged$ = new Subject<string>();
  defaultSoilAnalyses$ = this.store.pipe(select(selectDefaultSoilAnalyses));

  pageSize = 10;
  displayedColumns = ['id', 'description', 'state', 'stateChange', 'actions'];
  page$ = new BehaviorSubject<PageEvent>({
    pageIndex: 0,
    pageSize: this.pageSize,
    length: null,
  });

  filterConfig: FilterConfig<FiltersConfig> = {
    filters: {
      search: {
        label: 'search',
        type: FilterTypes.Text,
        changes: this.searchChanged$,
      },
    },
  };

  filterValue$ = this.searchChanged$.pipe(
    map((value: string) => value || ''),
    startWith(''),
  );

  filteredDefaultSoilAnalyses$ = combineLatest([this.filterValue$, this.defaultSoilAnalyses$]).pipe(
    map(([filter, defaultSoilAnalyses]) => {
      if (this.paginator) {
        this.paginator.firstPage();
      }

      return filter
        ? defaultSoilAnalyses.filter(
            (defaultSoilAnalysis) =>
              defaultSoilAnalysis.description.toLowerCase().indexOf(filter.toLowerCase()) !== -1 ||
              defaultSoilAnalysis.state.toString().toLowerCase().indexOf(filter.toLowerCase()) !== -1 ||
              defaultSoilAnalysis.id.toString().toLowerCase().indexOf(filter.toLowerCase()) !== -1,
          )
        : defaultSoilAnalyses;
    }),
    shareReplay(1),
  );

  pagedDefaultSoilAnalyses$ = combineLatest([this.filteredDefaultSoilAnalyses$, this.page$]).pipe(
    map(([defaultSoilAnalyses, { pageIndex, pageSize }]) => defaultSoilAnalyses.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize)),
  );

  filterEnabled$ = this.filterValue$.pipe(map((value) => !!value));
  isMobile$ = this.breakpoint.ltLg$;

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  page(event: PageEvent) {
    this.page$.next(event);
  }

  addDefaultSoilAnalysis() {
    this.store.dispatch(SoilAnalysisActions.addDefaultSoilAnalysis());
  }

  editDefaultSoilAnalysis(defaultSoilAnalysis: SoilAnalysis) {
    this.store.dispatch(SoilAnalysisActions.editDefaultSoilAnalysis({ defaultSoilAnalysis }));
  }

  changeState(defaultSoilAnalysis: SoilAnalysis) {
    const targetState = defaultSoilAnalysis.state === State.Draft || defaultSoilAnalysis.state === State.InActive ? State.Active : State.InActive;
    this.store.dispatch(
      SoilAnalysisActions.changeStateDefaultSoilAnalysis({
        id: defaultSoilAnalysis.id,
        state: targetState,
      }),
    );
  }
}
